import type { Auth, User } from "firebase/auth";
import { useEffect, useState } from "react";
import * as api from "./api";
import { readMenuAll, writeMenuAll } from "./nav";
import { type Saved, saveFailure } from "./save";
import { Status } from "./UserPages";
import { Skeleton } from "./Skeleton";

export const OPEN_ALL_NOTE =
  "On a new phone or computer, the side menu then shows all its groups open. It does not change the menu that you have open now: use the button with two chevrons at the top right of the menu.";
export const NOT_AVAILABLE = "Not available yet.";

/** The "open all menu groups" setting of the person (U-34): the cached value at once, then the server value. */
export function useMenuAll(user: User): boolean | undefined {
  const [on, setOn] = useState(() => readMenuAll(user.uid));
  useEffect(() => {
    let live = true;
    const started = performance.now();
    user
      .getIdToken()
      .then(api.menuPrefs)
      .then((found) => {
        writeMenuAll(user.uid, found.open_all);
        if (live) setOn(found.open_all);
        console.info(`menu prefs: open_all=${found.open_all} in ${Math.round(performance.now() - started)} ms`);
      })
      .catch((e) => {
        const status = e instanceof api.ApiError ? e.status : 0;
        // An API without /api/me/menu keeps the menu as before: the groups stay as the person left them.
        if (status === 404 && live) setOn(false);
        console.warn(`menu prefs: load failed (${status || "network"})`);
      });
    return () => {
      live = false;
    };
  }, [user]);
  return on;
}

export function MenuSettings({ auth, user }: { auth: Auth; user: User }) {
  const [found, setFound] = useState<api.MenuPrefs | "failed" | "missing" | undefined>(undefined);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(api.menuPrefs)
      .then((next) => live && setFound(next))
      .catch((e) => live && setFound(e instanceof api.ApiError && e.status === 404 ? "missing" : "failed"));
    return () => {
      live = false;
    };
  }, [user, attempt]);
  const change = async (on: boolean) => {
    const before = found;
    if (saving || before === undefined || typeof before === "string") return;
    const started = performance.now();
    setFound({ open_all: on });
    setSaving(true);
    setSaved(null);
    try {
      const next = await api.setMenuPrefs(await user.getIdToken(), { open_all: on });
      writeMenuAll(user.uid, next.open_all);
      setFound(next);
      setSaved({ ok: true, text: `Saved: all menu groups open by default is ${next.open_all ? "on" : "off"}.` });
      console.info(`menu prefs: saved open_all=${next.open_all} in ${Math.round(performance.now() - started)} ms`);
    } catch (e) {
      setFound(before);
      const failed = saveFailure(e, "The setting");
      setSaved({ ...failed, text: `Not saved: the setting is still ${before.open_all ? "on" : "off"}. ${failed.text}` });
      console.warn("menu prefs: save failed", e);
    } finally {
      setSaving(false);
    }
  };
  return (
    <fieldset data-testid="menu-settings">
      <legend>Menu</legend>
      {found === undefined ? (
        <Skeleton shape="form" />
      ) : found === "missing" ? (
        <p className="sub" data-testid="menu-open-all-missing">
          {NOT_AVAILABLE}
        </p>
      ) : found === "failed" ? (
        <p className="error">
          Cannot load your menu setting.{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : (
        <>
          <label>
            <input
              type="checkbox"
              data-testid="menu-open-all"
              checked={found.open_all}
              aria-disabled={saving}
              onChange={(event) => void change(event.target.checked)}
            />
            Open all menu groups by default
          </label>
          <p className="hint check-help">{OPEN_ALL_NOTE}</p>
        </>
      )}
      <Status saved={saved} auth={auth} testId="menu-settings-status" />
    </fieldset>
  );
}
